import React, { useState, useEffect, useRef, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { transformPaddingToTailwind, computeLightOrDark } from '../../helpers/style'
import { MarkupContext } from '../../providers/Markup'
import { JourneyGateContext } from '../../providers/JourneyGate'
import { MemberSidebarMenu, MemberSidebarMenuDevice } from '../helpers/member/MemberSidebarMenu'
import { dataLayerPush } from '../../helpers/thirdparty'
import { useMemberSession } from '../../helpers/memberSession'
import ButtonEl from '../helpers/ctas/Button'

// Components that can appear inside a Member Area Container. Mirrors the inner
// registry of ArticleContainer.jsx but adds the member-area-only blocks:
//   - memberMyDetailsForm / memberOrganisationDetailsForm /
//     memberOrganisationInformationForm / staffMyProfileForm (each emits the
//     same MemberSelfServiceForms shape with a single-form list)
//   - renewalsEmbedForm, registrationComponent, renewButton (existing member flow)
import Accordion from './Accordion'
import BasicContent from './BasicContent'
import BasicContentWithColumns from './BasicContentWithColumns'
import CalloutCard from './CalloutCard'
import CardListCurated from './CardListCurated'
import CardListDynamic from './CardListDynamic'
import ContentFiftybyFifty from './ContentFiftybyFifty'
import HeroBanner from './HeroBanner'
import HeroCard from './HeroCard'
import InformationBanner from './InformationBanner'
import InformationBox from './InformationBox'
import SearchResult from './SearchResult'
import SubPageBanner from './SubPageBanner'
import ThirdParty from './ThirdParty'
import TagListing from './TagListing'
import TestimonialCarousel from './TestimonialCarousel'
import StepsCards from './StepsCards'
import JumpToSection from './JumpToSection'
import MemberSelfServiceForms from './MemberSelfServiceForms'
import RenewalsEmbedForm from './RenewalsEmbedForm'
import RegistrationForm from './RegistrationForm'
import RenewButton from './RenewButton'
import InvoicePage from './InvoicePage'
import PaymentFormPage from './PaymentFormPage'
import PendingApprovalPage from './PendingApprovalPage'
import ReceiptDownloadPage from './ReceiptDownloadPage'
import Alert from '../furniture/Alert'
import { getMemberStatusAlertProps } from '../../helpers/memberStatusConfig'

// ── Member-portal section access ─────────────────────────────────────────────
// Verified against the live D365 forms by their rendered <h1>:
//   staffMyProfileForm              → "My Profile"            (all members)
//   memberMyDetailsForm             → "Membership Details"    (privileged only)
//   memberOrganisationDetailsForm   → "Organisation Details"  (privileged only)
//   memberOrganisationInformationForm → "Organisation Information" (privileged only)
//
// Only the "My Profile" section is visible to every authenticated member.
const PROFILE_ALIASES = ['staffMyProfileForm']
// All member-portal form sections — used to scope the Industry Supporter filter
// so generic content blocks placed in the member area are left untouched.
const MEMBER_FORM_ALIASES = [
  'memberMyDetailsForm',
  'memberOrganisationDetailsForm',
  'memberOrganisationInformationForm',
  'staffMyProfileForm',
  'memberSelfServiceForms',
]
// Membership / organisation sections visible only to Main/Administrator/Finance.
// NOTE: the bundled `memberSelfServiceForms` block cannot be split per-section,
// so it is treated as privileged-only and is hidden from Industry Supporters.
// For Industry Supporters to see "My Profile", build the page with the
// per-section blocks (staffMyProfileForm = My Profile), not the bundle.
const PRIVILEGED_ONLY_ALIASES = [
  'memberMyDetailsForm',
  'memberOrganisationDetailsForm',
  'memberOrganisationInformationForm',
  'memberSelfServiceForms',
]
// Used ONLY by the renewal status-banner tag below (already fail-closed).
// Section/sidebar gating no longer reads role names client-side — it consumes
// the server-computed `canAccessPortalSections` session flag instead, so the
// allowlist lives in one place (MembershipRules.IsPrivilegedPortalRole).
const PRIVILEGED_ROLES = ['main', 'administrator', 'finance']

// The left-hand sidebar links come back from Umbraco as a full list, but they
// map 1:1 to the member-portal form sections, so the navigation must follow the
// SAME role/category gating as the rendered sections. The path → alias map is
// built from the CrmMember page URLs returned by the member-context endpoint
// (never hardcoded here), keyed by normalised path (lower-case, no trailing
// slash). Any link whose path isn't in the map is treated as generic and always
// shown. `portalUrls` is null until the session resolves — the privileged
// sections themselves stay hidden regardless while that loads (fail closed).
const buildSidebarUrlAliases = (portalUrls) => {
  if (!portalUrls) return {}
  return Object.fromEntries(
    [
      [portalUrls.myProfile, 'staffMyProfileForm'],
      [portalUrls.membershipDetails, 'memberMyDetailsForm'],
      [portalUrls.organisationDetails, 'memberOrganisationDetailsForm'],
      [portalUrls.organisationInformation, 'memberOrganisationInformationForm'],
    ]
      .map(([url, alias]) => [sidebarPathFromUrl(url), alias])
      .filter(([path]) => path !== ''),
  )
}

// NOTE: the denied direct-URL redirect (a member without portal-section access
// opening a privileged portal page) is handled app-wide in App.jsx
// (redirectDeniedPortalPage), NOT here — a stripped page may not mount this
// container at all, so the redirect cannot live inside it.

const isIndustrySupporterCategory = (category) =>
  typeof category === 'string' && category.toLowerCase().includes('industry')
const isPrivilegedRole = (role) =>
  typeof role === 'string' && PRIVILEGED_ROLES.includes(role.trim().toLowerCase())

// Normalise a sidebar URL to a comparable path: drop origin/query/hash,
// lower-case, and strip the trailing slash so '/member/my-profile/' matches the
// SIDEBAR_URL_ALIASES keys regardless of how Umbraco emits the link.
const sidebarPathFromUrl = (url) => {
  if (typeof url !== 'string') return ''
  let path = url.split(/[?#]/)[0]
  const schemeIdx = path.indexOf('://')
  if (schemeIdx !== -1) {
    const afterScheme = path.slice(schemeIdx + 3)
    const firstSlash = afterScheme.indexOf('/')
    path = firstSlash === -1 ? '' : afterScheme.slice(firstSlash)
  }
  return path.replace(/\/+$/, '').toLowerCase()
}

// Shared gating predicate for both the rendered sections and the sidebar links,
// so the two can never drift apart. `industry` / `portalSectionsAllowed` are
// derived once from the resolved session (see the fail-closed note below).
const isAliasAccessible = (alias, { industry, portalSectionsAllowed }) => {
  // Industry Supporters may see "My Profile" only — hide the other portal forms.
  if (industry && MEMBER_FORM_ALIASES.includes(alias) && !PROFILE_ALIASES.includes(alias)) {
    return false
  }
  // Membership / organisation sections are allowlisted roles only. FAIL CLOSED:
  // hidden unless the server explicitly granted access (see MembershipRules
  // .CanAccessMemberPortalSections) — an unknown/blank role means denied.
  if (PRIVILEGED_ONLY_ALIASES.includes(alias) && !portalSectionsAllowed) {
    return false
  }
  return true
}

const MemberAreaContainer = (props) => {
  const { getPaletteColor } = useContext(MarkupContext)
  // While a nested journey status check may still redirect the member, hide the member-status
  // banner and every sibling component other than the gate source.
  const { gateActive, gateSource } = useContext(JourneyGateContext)
  // Sidebar visibility is driven by the `sidebarOnOff` API flag. Default to the
  // prop value so the sidebar column doesn't flash in/out on mount.
  const [useSidebar, setUseSidebar] = useState(props.sidebarOnOff ?? true)
  const {
    needsRenewal,
    role,
    memberCategory,
    status,
    canAccessPortalSections,
    portalUrls,
    loaded: sessionLoaded,
  } = useMemberSession()
  // FAIL CLOSED: privileged sections/links stay hidden while the session loads
  // and whenever the server did not explicitly grant access. The server has
  // already stripped them from pageData for denied members (MemberPortalSection-
  // GateService) — this client-side mirror only prevents a stale/bundled render.
  const portalSectionsAllowed = sessionLoaded && canAccessPortalSections === true
  // Renewal-journey banner is FAIL-CLOSED: an unknown/missing role hides it (a deliberate
  // deviation from the fail-open section gating below) so it stays purely gated to
  // Main/Administrator/Finance. Non-renewal banners (lapsed, new-application payment, bare
  // pending approval) are untagged and remain visible to all roles.
  const memberStatusAlertProps = getMemberStatusAlertProps(status, props.statusAlerts, isPrivilegedRole(role))

  // Filter member-area blocks by the signed-in member's session. FAIL CLOSED:
  // the privileged membership/organisation sections render only after the
  // session resolves with the server-computed allowlist verdict set to true —
  // an unknown/blank/unsynced role is denied by default. "My Profile" and
  // generic content blocks are unaffected.
  const accessibleComponents = useMemo(() => {
    const list = props.memberAreaComponents || []
    const industry = isIndustrySupporterCategory(memberCategory)

    return list.filter((component) =>
      isAliasAccessible(component.componentName, { industry, portalSectionsAllowed }),
    )
  }, [props.memberAreaComponents, portalSectionsAllowed, memberCategory])

  // The four member-area form aliases all share the MemberSelfServiceForms
  // payload shape (single-item Forms list), so they all route to the same
  // React component.
  const components = {
    accordion: Accordion,
    basicContent: BasicContent,
    basicContentWithColumns: BasicContentWithColumns,
    calloutCard: CalloutCard,
    cardListCurated: CardListCurated,
    cardListDynamic: CardListDynamic,
    contentFiftybyFifty: ContentFiftybyFifty,
    heroBanner: HeroBanner,
    heroCard: HeroCard,
    informationBanner: InformationBanner,
    informationBox: InformationBox,
    searchResult: SearchResult,
    subPageBanner: SubPageBanner,
    thirdParty: ThirdParty,
    tagListing: TagListing,
    testimonials: TestimonialCarousel,
    stepsCards: StepsCards,
    jumpToSection: JumpToSection,
    memberSelfServiceForms: MemberSelfServiceForms,
    memberMyDetailsForm: MemberSelfServiceForms,
    memberOrganisationDetailsForm: MemberSelfServiceForms,
    memberOrganisationInformationForm: MemberSelfServiceForms,
    staffMyProfileForm: MemberSelfServiceForms,
    renewalsEmbedForm: RenewalsEmbedForm,
    registrationComponent: RegistrationForm,
    renewButton: RenewButton,
    invoicePage: InvoicePage,
    paymentFormPage: PaymentFormPage,
    pendingApprovalPage: PendingApprovalPage,
    receiptDownloadPage: ReceiptDownloadPage,
  }

  const sidebarTitle = props.sidebarTitle || 'Jump to a section'
  const [isLarge, setIsLarge] = useState(false)
  const resizeTimeoutRef = useRef(null)

  // The sidebar menu is built from the container's `sidebarUrls` API data — a
  // list of cross-page links to the sibling member-area pages. `isActive` is
  // resolved server-side (current-page match), so there is no in-page scroll-spy.
  // Each link is gated by the same rule as its destination section (via the
  // config-driven path → alias map), so a member never sees a link to a section
  // they can't access. FAIL CLOSED for privileged links (hidden until the server
  // grants access); links that don't map to a member-portal section are always
  // shown. This mirrors the server-side stripping in MemberPortalSectionGate-
  // Service — denied members' privileged links never reach the payload anyway.
  const navigationItems = useMemo(() => {
    const industry = isIndustrySupporterCategory(memberCategory)
    const sidebarUrlAliases = buildSidebarUrlAliases(portalUrls)

    return (props.sidebarUrls || [])
      .filter((entry) => entry && entry.url)
      .filter((entry) => {
        const alias = sidebarUrlAliases[sidebarPathFromUrl(entry.url)]
        if (!alias) return true
        return isAliasAccessible(alias, { industry, portalSectionsAllowed })
      })
      .map((entry) => ({
        url: entry.url,
        label: entry.title,
        isActive: !!entry.isActive,
      }))
  }, [props.sidebarUrls, portalSectionsAllowed, memberCategory, portalUrls])

  // Only two presentations are needed: a stacked device menu below the large
  // breakpoint and the sticky desktop menu at/above it.
  const updateBreakpoints = () => {
    setIsLarge(window.matchMedia('(min-width: 1024px)').matches)
  }

  const handleResize = () => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current)
    }
    resizeTimeoutRef.current = setTimeout(updateBreakpoints, 100)
  }

  useEffect(() => {
    updateBreakpoints()
    window.addEventListener('resize', handleResize, { passive: true })
    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [])

  // Keep sidebar visibility in sync with the API flag.
  useEffect(() => {
    setUseSidebar(props.sidebarOnOff ?? true)
  }, [props.sidebarOnOff])

  const handleNavigationClick = (item) => {
    dataLayerPush({
      event: 'navigation_menu_click',
      click_text: item?.label,
      destination_path: item?.url,
    })
  }

  const [backgroundColor, setBackgroundColor] = useState('transparent')
  const [lightOrDarkValue, setLightOrDarkValue] = useState('light')

  useEffect(() => {
    setLightOrDarkValue(computeLightOrDark(backgroundColor, getPaletteColor))
  }, [backgroundColor, getPaletteColor])

  useEffect(() => {
    const bgColor = backgroundColor.replace('#', '')
    setBackgroundColor((prevState) => (bgColor ? `#${bgColor}` : prevState))
    setLightOrDarkValue(computeLightOrDark(bgColor, getPaletteColor))
    // Intentionally mount-only (legacy): reads initial backgroundColour state only.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [])

  const renderComponent = (component) => {
    const { componentName, componentData, jumpToId } = component
    const ComponentToRender = components[componentName]

    if (ComponentToRender) {
      delete componentData.key
    } else {
      console.warn(`Component type ${componentName} was not found in MemberAreaContainer. Ignoring...`)
    }

    return (
      <div id={jumpToId} className="scroll-mt-24">
        {ComponentToRender ? (
          <ComponentToRender {...componentData} fromMemberAreaContainer={true} isSectionVisible={true} />
        ) : null}
      </div>
    )
  }

  const componentPadding = transformPaddingToTailwind(props.componentPadding, false, props.breadcrumbs)

  // Every child renders into the DOM so mount-time integrations (notably embedded
  // D365 forms, which FormLoader.bundle.js only initialises for the [data-form-id]
  // nodes present when it loads) come up once. The sidebar now links across pages,
  // so there is no in-page section-switching — all components on this page show.
  const allComponents = accessibleComponents

  return (
    <section
      data-type="memberAreaContainer"
      data-index={props.idx}
      style={{
        '--mobile-bg-url': props.mobileBackgroundImage ? `url(${props.mobileBackgroundImage.src})` : null,
        '--desktop-bg-url': props.desktopBackgroundImage ? `url(${props.desktopBackgroundImage.src})` : null,
        '--bg-color': backgroundColor ?? 'transparent',
      }}
      className={componentPadding}
    >
      <div className="container mx-auto">
        {!gateActive && sessionLoaded && memberStatusAlertProps &&
          !window.location.pathname.startsWith('/member/receipt') &&
          !window.location.pathname.startsWith('/member/payment') && (
            <div className="mb-6">
              <Alert {...memberStatusAlertProps} />
            </div>
          )}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-20 items-start">
          {useSidebar && navigationItems.length > 0 && (
            <div className="col-span-full lg:col-span-1 lg:sticky lg:top-24">
              {!isLarge && (
                <MemberSidebarMenuDevice
                  navigationItems={navigationItems}
                  sidebarTitle={sidebarTitle}
                  onItemClick={handleNavigationClick}
                />
              )}
              {isLarge && (
                <MemberSidebarMenu
                  navigationItems={navigationItems}
                  sidebarTitle={sidebarTitle}
                  onItemClick={handleNavigationClick}
                />
              )}
              {needsRenewal && !window.location.pathname.startsWith('/member/receipt') && !window.location.pathname.startsWith('/member/payment') && (
                <div className="mt-6 px-4 sm:px-6 xl:px-0">
                  <ButtonEl
                    theme="primary"
                    link={{ url: '/member/renewals', name: 'Renew membership' }}
                    className="w-full justify-center"
                    onClick={() => {
                      dataLayerPush({
                        event: 'navigation_menu_click',
                        category: 'Renew',
                        click_text: 'Renew membership',
                      })
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Main Content */}
          <div
            className={`col-span-full px-4 sm:px-6 lg:px-0 ${navigationItems.length > 0 && useSidebar ? 'lg:col-span-3' : 'lg:col-span-4'}`}
            data-jump-scan-root
          >
            <div className="space-y-12">
              {allComponents.length > 0 ? (
                allComponents.map((component, index) => (
                  // CSS-hide (not unmount) siblings while the journey gate is closed — embedded
                  // D365 forms need their [data-form-id] nodes in the DOM when FormLoader loads.
                  <div
                    key={`member-area-component-${component.jumpToId || index}`}
                    className={gateActive && component.componentName !== gateSource ? 'hidden' : undefined}
                  >
                    {renderComponent(component)}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-grey">No member-area components found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

MemberAreaContainer.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  sidebarTitle: PropTypes.string,
  sidebarOnOff: PropTypes.bool,
  sidebarUrls: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
      isActive: PropTypes.bool,
    }),
  ),
  variant: PropTypes.string,
  componentPadding: PropTypes.string,
  breadcrumbs: PropTypes.bool,
  mobileBackgroundImage: PropTypes.object,
  desktopBackgroundImage: PropTypes.object,
  backgroundColor: PropTypes.string,
  idx: PropTypes.number,
  memberAreaComponents: PropTypes.arrayOf(
    PropTypes.shape({
      componentName: PropTypes.string.isRequired,
      componentData: PropTypes.object,
      jumpToId: PropTypes.string,
      sidebarMenuTitle: PropTypes.string,
    }),
  ),
}

export default MemberAreaContainer
